import { ListItemIcon, ListItemText, MenuItem, Typography } from "@mui/material";
import { MessageSeverity } from "@sinch/core/entities/serverEnums";
import { MdsLinkOff } from "@sinch/icons";
import { parseMutationErrorsFromData } from "@sinch/utils/apollo";
import { useSnackbar } from "notistack";
import { propEq } from "ramda";
import { isNotNilOrEmpty } from "ramda-adjunct";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { MdsIcon } from "@sinch/components";
import { useRatelist } from "@sinch/components/application/Rate/useRatelist";
import { ConfirmDialogWrapper } from "@sinch/components/ConfirmDialogWrapper/ConfirmDialogWrapper";

export const RemoveFromCompanyMenuItem = ({
  id,
  companyId,
  onPerformedAction,
}: {
  id: string;
  companyId: string;
  onPerformedAction: () => void;
}) => {
  const { t } = useTranslation(["common", "draft"]);
  const { unlinkFromCompany } = useRatelist();
  const { enqueueSnackbar } = useSnackbar();

  const handleConfirm = useCallback(async () => {
    try {
      const response = await unlinkFromCompany({ variables: { ratelistId: id, companyId } });
      const firstMessage = parseMutationErrorsFromData(response).find((message) =>
        propEq("severity", MessageSeverity.Error)(message)
      )?.message;
      if (isNotNilOrEmpty(firstMessage)) {
        enqueueSnackbar(firstMessage, { variant: "error" });
      }
      onPerformedAction();
    } catch {
      enqueueSnackbar(t("ratelistError"), { variant: "error" });
    }
  }, [unlinkFromCompany, id, companyId, onPerformedAction, enqueueSnackbar, t]);

  return (
    <ConfirmDialogWrapper
      content={<Typography>{t("CompanyRatelist.confirmRemoveFromCompany")}</Typography>}
      label={t("CompanyRatelist.removeFromCompany")}
      onConfirm={handleConfirm}
      render={({ openConfirmation }) => (
        <MenuItem color="warning" onClick={openConfirmation}>
          <ListItemIcon>
            <MdsIcon color="warning" icon={MdsLinkOff} />
          </ListItemIcon>
          <ListItemText sx={{ color: "warning.main" }}>{t("CompanyRatelist.removeFromCompany")}</ListItemText>
        </MenuItem>
      )}
    />
  );
};
